import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FieldTitle, InputHelperText, useInput } from 'react-admin';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  OutlinedInput,
  Popover,
  TextField,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { AsYouType, CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import { ICountry, useCountries } from '../../shared/hooks/api/useCountryStateCity';
import { IAccessLevel } from '../../shared/utils/policyCheck';
import { useInputWithPolicies } from './useInputWithPolicies';
import { CommonTextInputProps } from './CommonTextInput';

export type CommonPhoneInputProps = {
  ignorePolicies?: boolean;
} & Omit<CommonTextInputProps, 'format' | 'parse'> &
  IAccessLevel;

function makePlaceholder(isoCode: string): string {
  const sample = new AsYouType(isoCode as CountryCode).input('5550000000');
  return sample.replace(/\d/g, '_');
}

const PhoneInputCore = (props: CommonPhoneInputProps) => {
  const {
    source,
    validate,
    label,
    fullWidth,
    disabled,
    helperText,
    level,
    ignorePolicies,
    ...rest
  } = props;

  const {
    field,
    fieldState: { error, isTouched },
    formState: { isSubmitted },
    isRequired,
  } = useInput({ source, validate, ...rest });

  const { data: countries = [], isLoading } = useCountries();
  const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(null);
  const [digits, setDigits] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);
  const userHasTypedRef = useRef(false);

  const displayValue = useMemo(() => {
    if (!digits) return '';
    if (selectedCountry)
      return new AsYouType(selectedCountry.iso2 as CountryCode).input(digits);
    return digits;
  }, [digits, selectedCountry]);

  const placeholder = useMemo(
    () => (selectedCountry ? makePlaceholder(selectedCountry.iso2) : ''),
    [selectedCountry]
  );

  useEffect(() => {
    if (userHasTypedRef.current || !countries.length) return;
    const currentValue = field.value;
    if (currentValue) {
      initializedRef.current = true;
      try {
        const valueStr = String(currentValue);
        const normalizedValue = valueStr.startsWith('+') ? valueStr : `+${valueStr}`;
        const parsed = parsePhoneNumberWithError(normalizedValue);
        if (parsed?.country) {
          setSelectedCountry(countries.find((c) => c.iso2 === parsed.country) ?? null);
          setDigits(parsed.nationalNumber);
          return;
        }
        if (parsed?.countryCallingCode) {
          const iso2 = parsed.countryCallingCode === '1' ? 'US' : undefined;
          const country =
            countries.find((c) =>
              iso2 ? c.iso2 === iso2 : c.phonecode === parsed.countryCallingCode
            ) ?? null;
          if (country) {
            setSelectedCountry(country);
            setDigits(parsed.nationalNumber);
            return;
          }
        }
      } catch {}
      setDigits(String(currentValue).replace(/\D/g, ''));
      return;
    }
    if (!initializedRef.current) {
      initializedRef.current = true;
      setSelectedCountry(countries.find((c) => c.iso2 === 'US') ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, field.value]);

  const storePhoneValue = (country: ICountry | null, rawDigits: string) => {
    if (!rawDigits || !country) {
      field.onChange(rawDigits || '');
      return;
    }
    const formatter = new AsYouType(country.iso2 as CountryCode);
    formatter.input(rawDigits);
    field.onChange(formatter.getNumber()?.number ?? `+${country.phonecode}${rawDigits}`);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    userHasTypedRef.current = true;
    const inputType = (e.nativeEvent as InputEvent).inputType ?? '';
    const isDeleting = inputType.startsWith('delete');
    const newRawDigits = e.target.value.replace(/\D/g, '');
    let finalDigits = newRawDigits;
    if (isDeleting && newRawDigits.length >= digits.length && digits.length > 0) {
      finalDigits = digits.slice(0, -1);
    }
    setDigits(finalDigits);
    storePhoneValue(selectedCountry, finalDigits);
  };

  const handleOpenPicker = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setSearch('');
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleClosePicker = () => setAnchorEl(null);

  const handleSelectCountry = (country: ICountry) => {
    setSelectedCountry(country);
    setDigits('');
    field.onChange('');
    handleClosePicker();
  };

  const filteredCountries = useMemo(() => {
    if (!search) return countries;
    const q = search.toLowerCase();
    return countries.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.phonecode.includes(q) ||
        o.iso2.toLowerCase().startsWith(q)
    );
  }, [countries, search]);

  const hasError = !!error;
  const isOpen = Boolean(anchorEl);
  const showError = (isTouched || isSubmitted) && !!error?.message;

  const resolvedLabel =
    label !== false
      ? label || (
          <FieldTitle label={label as string} source={source} isRequired={isRequired} />
        )
      : undefined;

  const countryLabel = selectedCountry
    ? `${selectedCountry.emoji} +${selectedCountry.phonecode}`
    : '🌐';

  return (
    <Box sx={{ mb: 3 }}>
      <FormControl
        fullWidth={fullWidth}
        error={hasError}
        variant="outlined"
        disabled={disabled}
        required={isRequired}
        sx={{ position: 'relative' }}
      >
        {resolvedLabel && <InputLabel>{resolvedLabel}</InputLabel>}
        {showError && (
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              transform: 'translateY(-100%)',
              pb: '2px',
              fontSize: '0.75rem',
              color: 'error.main',
            }}
          >
            <InputHelperText touched error={error!.message} helperText={false} />
          </Box>
        )}
        <OutlinedInput
          label={resolvedLabel}
          value={displayValue}
          onChange={handleNumberChange}
          onBlur={field.onBlur}
          inputRef={field.ref}
          placeholder={placeholder}
          startAdornment={
            <InputAdornment position="start" sx={{ mr: 0 }}>
              <Button
                onClick={handleOpenPicker}
                disabled={disabled || isLoading}
                disableRipple
                disableFocusRipple
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0,
                  height: '100%',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 400,
                  letterSpacing: 0,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0,
                  mr: 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                {isLoading ? <CircularProgress size={16} /> : countryLabel}
                <ArrowDropDownIcon sx={{ ml: 0.5, fontSize: 20, opacity: 0.6 }} />
              </Button>
            </InputAdornment>
          }
        />
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClosePicker}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { width: 320 } }}
        >
          <Box sx={{ p: 1.5 }}>
            <TextField
              inputRef={searchRef}
              fullWidth
              size="small"
              placeholder="Search country…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
          <List dense disablePadding sx={{ maxHeight: 280, overflow: 'auto' }}>
            {filteredCountries.map((country) => (
              <ListItemButton
                key={country.iso2}
                selected={selectedCountry?.iso2 === country.iso2}
                onClick={() => handleSelectCountry(country)}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '1.2rem' }}>{country.emoji}</span>
                      <span style={{ flexGrow: 1 }}>{country.name}</span>
                      <span style={{ color: '#888', fontSize: '0.85rem' }}>
                        +{country.phonecode}
                      </span>
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
            {!filteredCountries.length && (
              <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                No results
              </Box>
            )}
          </List>
        </Popover>
      </FormControl>
    </Box>
  );
};

const CommonPhoneInput = (props: CommonPhoneInputProps) => {
  return useInputWithPolicies(
    <PhoneInputCore {...props} />,
    props.source,
    props.level,
    props.ignorePolicies
  );
};

export default CommonPhoneInput;
